import React, { FC, HTMLAttributes } from 'react';
import styled from 'styled-components';

const Pill = styled.div<{ $warning?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    background: ${(props) => (props.$warning ? '#fef2f2' : '#f1f5f9')};
    color: ${(props) => (props.$warning ? '#dc2626' : '#475569')};
    border: 1px solid ${(props) => (props.$warning ? '#fecaca' : '#e2e8f0')};
`;

const Divider = styled.span<{ $warning?: boolean }>`
    align-self: stretch;
    width: 1px;
    background: ${(props) => (props.$warning ? '#fecaca' : '#cbd5e1')};
`;

const Item = styled.span``;

export interface CounterBadgeItem {
    /** Testo che precede il valore, es. "Products:". Già localizzato dal chiamante. */
    label: React.ReactNode;
    value: React.ReactNode;
}

export interface CounterBadgeProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    /**
     * Contatori da mostrare, separati da un divisore verticale. Ignorato quando viene
     * passato `children`, che ha la precedenza per i casi a testo libero.
     */
    items?: CounterBadgeItem[];
    /** Variante di allerta: sfondo/bordo/testo rossi al posto degli slate. */
    warning?: boolean;
    children?: React.ReactNode;
    /**
     * React 19 passa `ref` come prop normale. Va inoltrato al nodo radice perché `ZTooltip`
     * clona il figlio per ancorarci il tooltip: senza ref il tooltip non si posiziona.
     */
    ref?: React.Ref<HTMLDivElement>;
}

/**
 * Pill informativa da barra strumenti, usata per esporre uno o più contatori accanto al
 * titolo di pagina o nella top bar della Test Mode (es. "Test orders: 12",
 * "Products: 128 | Published: 94").
 *
 * Due modalità d'uso, mutuamente esclusive:
 * - `items`: elenco di coppie label/valore, separate automaticamente da un divisore;
 * - `children`: contenuto libero, per i messaggi che non sono un contatore
 *   (es. "No Test Orders left. Buy more.").
 *
 * Non incorpora il tooltip: chi la usa la avvolge in `ZTooltip` con il testo che gli serve.
 */
export const CounterBadge: FC<CounterBadgeProps> = ({ items, warning, children, ...rest }) => (
    <Pill $warning={warning} {...rest}>
        {children ??
            items?.map((item, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <Divider $warning={warning} />}
                    <Item>
                        {item.label} {item.value}
                    </Item>
                </React.Fragment>
            ))}
    </Pill>
);
